import { TabsContent, TabsTrigger } from '@/components/ui/tabs'
import { ReactNode } from 'react'

/**
 * Configuration type for a Profit and Loss tab
 */
export type TabConfig = {
  value: string
  title: string
  content: ReactNode
  hasUnreadAlerts?: boolean
  onTabClick?: () => void
}

/**
 * Creates a Profit and Loss tab with both trigger and content components
 *
 * @param config - The configuration object containing value, title, and content
 * @returns An object with trigger and content components that can be used in a Tabs component
 *
 * @example
 * ```tsx
 * // Define your tab configurations
 * const tabConfigs = [
 *   {
 *     value: 'sales',
 *     title: 'Sales',
 *     content: <SalesComponent />
 *   },
 *   // ... more tabs
 * ];
 *
 * // Create tabs
 * const tabs = tabConfigs.map(config => createProfitAndLossTab(config));
 *
 * // Use in your component
 * return (
 *   <Tabs defaultValue="sales">
 *     <TabsList>
 *       {tabs.map(tab => tab.trigger)}
 *     </TabsList>
 *     {tabs.map(tab => tab.content)}
 *   </Tabs>
 * );
 * ```
 */
export const createTab = (config: TabConfig) => {
  return {
    trigger: (
      <TabsTrigger
        key={`trigger-${config.value}`}
        value={config.value}
        className="w-full relative"
        onClick={config.onTabClick}
      >
        <span className="flex items-center gap-2">
          {config.hasUnreadAlerts && (
            <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-2"></span>
          )}
          {config.title}
        </span>
      </TabsTrigger>
    ),
    content: (
      <TabsContent
        key={`content-${config.value}`}
        value={config.value}
        className="w-full"
      >
        {config.content}
      </TabsContent>
    ),
  }
}
